import React, { useState, useEffect } from 'react'

import { Card, CardContent, Grid2, Typography } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from "react-hook-form";
import * as Yup from "yup";

import { StateViewInterface } from "../interfaces/stateViews.interface";
import MButton from '../../../components/inputs/yup/materialui/mButton';
import InputText from '../../../components/inputs/yup/materialui/inputText';
import InputSelect from "../../../components/inputs/yup/materialui/inputSelect";
import InputFecha from "../../../components/inputs/yup/materialui/inputFecha";
import InputNumber from "../../../components/inputs/yup/materialui/inputNumber";
import { useLocation } from "react-router-dom";
import { useLoader } from "../../../hooks/useLoader";
import { useToast } from "../../../hooks/useToast";
import ConfirmationModal from "../../../components/ui/materialui/confirmationModal";
import { RespuestaSolicitudesPrepagoDto } from "../models";
import { SaveAlt, Close, PointOfSale, AttachMoney, Money } from '@mui/icons-material';
import { FormRecaudarPrepago } from '../interfaces/formRecaudarPrepago.Interface';
import { useRecaudarSolicitud } from '../hook/useRecaudarSolicitud';
import { recaudarPrepago } from '../services/servicePrepago';
import { StatusCode, TipoFuncionalidad } from "../../../utils/enums";
import { PermissionGuard } from '../../../components/auth/ProtectedRoute';

// Interfaz específica para la recaudación que coincide con SolicitudPrepagoRecaudoDto
interface DatosRecaudacion {
    mutuoId: number;
    solicitudId: number;
    formaPago: string;           // required en el backend
    sucursalPago: string;        // required en el backend
    fechaRecPrepago: Date;       // no nullable en el backend
    prepagoCliente: number | null; // nullable en el backend
}

interface Props {
    solicitud: RespuestaSolicitudesPrepagoDto | null,
    setViewToShow: (nameView: keyof StateViewInterface) => void;
    onRecaudacionSuccess?: () => void;
};

const schema = Yup.object({
    mutuoId: Yup.number().required("El mutuo es requerido"),
    sucursalPago: Yup.string().required("La sucursal de pago es requerida"),
    fechaRecaudacion: Yup.date(),
    formaPago: Yup.string().required("La forma de pago es requerida"),
    prepagoCliente: Yup.number().nullable(),
}) as Yup.ObjectSchema<FormRecaudarPrepago>;

const GenerarRecaudacion: React.FC<Props> = ({ setViewToShow, solicitud, onRecaudacionSuccess }: Props) => {

    const location = useLocation();
    const { showLoader, hideLoader } = useLoader();
    const { addToast } = useToast();

    const { id: idEmergente } = location.state || {}
    const [ modalConfirmacion, setModalConfirmacion ] = useState({
        show: false,
        title: '',
        message: '',
        metodo: () => {},
    });

    const { defaultValues, listFormaPago, listSucursalPago } = useRecaudarSolicitud(solicitud as any);

    // Log para debuggear la solicitud recibida
    useEffect(() => {
        console.log('GenerarRecaudacion - Solicitud recibida:', solicitud);
        console.log('GenerarRecaudacion - Solicitud ID:', solicitud?.id);
        console.log('GenerarRecaudacion - Solicitud Mutuo ID:', solicitud?.mutuoId);
    }, [solicitud]);

    const {
        formState: { errors },
        control,
        watch,
        handleSubmit,
        setValue,
        getValues
    } = useForm<FormRecaudarPrepago>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema),
        mode: 'onChange',
    });

    const fechaSolicitud = watch("fechaSolicitud");
    const fechaPago = watch("fechaPago");
    const fechaRecaudacion = watch("fechaRecaudacion");

    // Establecer fecha de recaudación por defecto
    useEffect(() => {
        if (!fechaRecaudacion) {
            setValue("fechaRecaudacion", new Date());
        }
    }, [fechaRecaudacion, setValue]);

    const onSubmit = async (data: FormRecaudarPrepago) => {
        try {
            showLoader();
            
            // Validar que los campos requeridos estén presentes
            if (!data.formaPago || !data.sucursalPago || !data.fechaRecaudacion) {
                showToast('Error', 'Danger', 'La forma de pago, la sucursal y la fecha de recaudación son campos requeridos');
                return;
            }

            // Validar que la solicitud tenga un ID válido
            if (!solicitud?.id || solicitud.id === 0) {
                showToast('Error', 'Danger', 'No se pudo obtener el ID de la solicitud');
                return;
            }
            
            // Solo enviamos los campos requeridos
            const datosRecaudacion: DatosRecaudacion = {
                mutuoId: solicitud?.mutuoId ? parseInt(solicitud.mutuoId) : 0,
                solicitudId: solicitud?.id || 0,
                formaPago: data.formaPago,
                sucursalPago: data.sucursalPago,
                fechaRecPrepago: data.fechaRecaudacion || new Date(),
                prepagoCliente: data.prepagoCliente || null
            };
            
            // Recaudar prepago
            const response = await recaudarPrepago(datosRecaudacion);
            
            // Log para debugging
            console.log('Respuesta de recaudarPrepago:', response);
            console.log('Status de la respuesta:', response?.status);
            
            // Si llegamos aquí, la recaudación fue exitosa (el interceptor maneja los errores)
            // Solo necesitamos verificar que la respuesta exista y tenga un status válido
            if (response && (response.status === StatusCode.Ok || response.status === StatusCode.Created)) {
                // Éxito: status 200 (Ok) o 201 (Created)
                showToast('Éxito', 'Success', 'Recaudación procesada correctamente');
                
                // Llamar al callback de éxito si existe
                if (onRecaudacionSuccess) {
                    onRecaudacionSuccess();
                } else {
                    // Fallback al comportamiento anterior
                    setViewToShow('showListadoSolicitudes');
                }
            } else {
                // Si no hay respuesta o el status no es exitoso, el interceptor ya manejó el error
                // Solo mostramos un mensaje genérico y mantenemos en la vista actual
                console.warn('Respuesta inesperada de recaudarPrepago:', response);
                return;
            }

        } catch (error) {
            // El interceptor ya maneja los errores y muestra los toasts
            // Solo loggeamos para debugging
            console.error('Error al procesar recaudación:', error);
            // No es necesario mostrar toast aquí, el interceptor ya lo hace
        } finally {
            hideLoader();
        }
    };

    const handleRecaudarClick = () => {
        setModalConfirmacion({
            show: true,
            title: 'Confirmar Recaudación de Prepago',
            message: '¿Está seguro que desea proceder con la recaudación?',
            metodo: () => {
                setModalConfirmacion({ ...modalConfirmacion, show: false });
                // Aquí se ejecuta el submit del formulario
                handleSubmit(onSubmit)();
            },
        });
    };

    const showToast = (title: string, tipo: "Success" | "Danger" | "Warning" | "Info" | "Dark" | "Light", mensaje: string) => {
        addToast({
            mostrar: true,
            titulo: title,
            tipo,
            mensaje,
        });
    }

    return (
        <Card sx={{ mb: 2, backgroundcolor: 'white' }} raised>
            <ConfirmationModal
                onHide={() =>
                    setModalConfirmacion({ ...modalConfirmacion, show: false })
                }
                onConfirm={modalConfirmacion.metodo}
                title={modalConfirmacion.title}
                show={modalConfirmacion.show}
                message={modalConfirmacion.message}
            />

            <CardContent>
                <form onSubmit={handleSubmit(onSubmit)}>

                        {/* TITULO + BOTONES */}
                        <Grid2 container alignItems="center" spacing={2}>
                            {/* TITULO */}
                            <Grid2 size={4}>
                                <Typography className='card-title' variant="h6">Recaudar Prepago</Typography>
                            </Grid2>

                            {/* BOTONES */}
                            <Grid2 container spacing={1} size={8} sx={{ textAlign: "right", justifyContent: 'flex-end' }}>
                                <PermissionGuard permissions={[TipoFuncionalidad.Administrar_prepago]}>
                                <MButton
                                    variant='contained'
                                    color='primary'
                                    startIcon={<PointOfSale />} 
                                    onClick={handleRecaudarClick}
                                >
                                    Recaudar
                                </MButton>
                                </PermissionGuard>

                                <MButton
                                    variant='contained'
                                    color={'inherit'}
                                    startIcon={<Close />}
                                    onClick={() => setViewToShow('showListadoSolicitudes')}
                                >
                                    Cancelar
                                </MButton>

                            </Grid2>
                        </Grid2>

                        {/* PRIMERA FILA */}
                        <Grid2 container alignItems="center" mt={2} spacing={2}>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputFecha
                                        esReadOnly
                                        fecha={fechaSolicitud}
                                        name="fechaSolicitud"
                                        control={control}
                                        label="Fecha de solicitud"
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputFecha
                                        esReadOnly
                                        fecha={fechaPago}
                                        name="fechaPago"
                                        control={control}
                                        label="Fecha de pago"
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        readonly
                                        label="Motivo prepago"
                                        name="motivo"
                                        control={control}
                                        error={errors.motivo}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        readonly
                                        label="Tipo de prepago"
                                        name="tipo"
                                        control={control}
                                        error={errors.tipo}
                                    />
                                </Grid2>
                        </Grid2>

                        {/* SEGUNDA FILA */}
                        <Grid2 container alignItems="center" mt={2} spacing={2}>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        readonly
                                        label="Modo pago"
                                        name="modo"
                                        control={control}
                                        error={errors.modo}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        readonly
                                        disabled
                                        decimales={0}
                                        prefix="$"
                                        label="Monto de prepago a capital"
                                        name="montoPrepagoCapital"
                                        control={control}
                                        error={errors.montoPrepagoCapital}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        readonly
                                        disabled
                                        decimales={0}
                                        prefix="$"
                                        label="Comision prepago"
                                        name="comision"
                                        control={control}
                                        error={errors.comision}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        readonly
                                        disabled
                                        decimales={0}
                                        prefix="$"
                                        label="Interés devengado"
                                        name="interesDevengado"
                                        control={control}
                                        error={errors.interesDevengado}
                                    />
                                </Grid2>
                        </Grid2>

                        {/* TERCERA FILA */}
                        <Grid2 container alignItems="center" mt={2} spacing={2}>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        readonly
                                        disabled
                                        decimales={0}
                                        prefix="$"
                                        label="Seguro mes prepago"
                                        name="segurosMes"
                                        control={control}
                                        error={errors.segurosMes}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        readonly
                                        disabled
                                        decimales={0}
                                        prefix="$"
                                        label="Monto dividendos impagos"
                                        name="montoDividendosImpagos"
                                        control={control}
                                        error={errors.montoDividendosImpagos}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        readonly
                                        disabled
                                        decimales={0}
                                        prefix="$"
                                        label="Interés penal dividendos"
                                        name="interesPenalDividendos"
                                        control={control}
                                        error={errors.interesPenalDividendos}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        readonly
                                        disabled
                                        decimales={0}
                                        prefix="$"
                                        label="Gastos de cobranza dividendos"
                                        name="gastosCobranzaDividendos"
                                        control={control}
                                        error={errors.gastosCobranzaDividendos}
                                    />
                                </Grid2>
                        </Grid2>

                        {/* CUARTA FILA */}
                        <Grid2 container alignItems="center" mt={2} spacing={2}>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        readonly
                                        disabled
                                        decimales={0}
                                        prefix="$"
                                        label="Total a pagar"
                                        name="totalPagar"
                                        control={control}
                                        error={errors.totalPagar}
                                    />
                                </Grid2>
                        </Grid2>

                        {/* DATOS ADICIONALES */}
                        <Grid2 container alignItems="center" spacing={2} mt={3}>
                            <Grid2 size={4}>
                                <Typography className='card-title' variant="h6">Datos del Prepago</Typography>
                            </Grid2>
                        </Grid2>

                        <Grid2 container alignItems="center" mt={1} spacing={2}>
                            <Grid2 size={{ xs: 12, lg: 3 }}>
                                <InputSelect
                                    clearable={false}
                                    label="Sucursal de pago"
                                    name="sucursalPago"
                                    control={control}
                                    options={listSucursalPago}
                                    error={errors.sucursalPago}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, lg: 3 }}>
                                <InputFecha
                                    label='Fecha de recaudación'
                                    name="fechaRecaudacion"
                                    control={control}
                                    fecha={fechaRecaudacion ?? new Date()}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, lg: 3 }}>
                                <InputSelect
                                    clearable={false}
                                    label="Forma de pago"
                                    name="formaPago"
                                    control={control}
                                    options={listFormaPago}
                                    error={errors.formaPago}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, lg: 3 }}>
                                <InputNumber
                                    decimales={0}
                                    prefix="$"
                                    label="Prepago del cliente"
                                    name="prepagoCliente"
                                    control={control}
                                    error={errors.prepagoCliente}
                                />
                            </Grid2>
                        </Grid2>
                    </form>
            </CardContent>
        </Card>
    )
}

export default GenerarRecaudacion;